import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import PanelListWithImages from '../components/PanelListWithImages';
import PanelListLinks from '../components/PanelListLinks';
import PanelListText from '../components/PanelListText';
import PanelListVideos from '../components/PanelListVideos';
import JourneyOverview from '../components/JourneyOverview';
import StepCard from '../components/StepCard';
import StepHelpButton from '../components/StepHelpButton';
import MeetingsTabContent from '../components/MeetingsTabContent';
import Workshop2026MeetingsCard from '../components/Workshop2026MeetingsCard';
import { usePageTracking } from '../hooks/usePageTracking';
import { AdminService, ProgramConfig } from '../services/adminService';
import { getSiteConfig } from '../services/siteConfigService';
import { progressService, ProgressItem } from '../services/progressService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/saaHomePage.module.css';

/** Set true when ready to show the Meetings & Workshops tab on SAA home. */
const SHOW_SAA_MEETINGS_TAB = false;

interface SAAHomePageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
}

const SAAHomePage: React.FC<SAAHomePageProps> = ({ user, onLogout, onLogin }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState<ProgramConfig | null>(null);
  const [meetingsEnabled, setMeetingsEnabled] = useState(true);
  const [workshopsTabShowFullFinder, setWorkshopsTabShowFullFinder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();
  
  // Determine active tab from URL or default. Tab order: Resources | Meetings | Work my steps. Default: Resources.
  const getInitialTab = (): 'tab1' | 'tab2' | 'tab3' => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'resources') return 'tab2';
    if (tabParam === 'meetings') return SHOW_SAA_MEETINGS_TAB ? 'tab3' : 'tab2';
    if (tabParam === 'steps') return 'tab1';
    return 'tab2'; // Resources is default for everyone
  };
  
  const [activeTab, setActiveTab] = useState<'tab1' | 'tab2' | 'tab3'>(getInitialTab());
  
  // Sync activeTab state when URL changes (for browser back/forward buttons)
  // Note: This only runs when searchParams changes, not when user state changes.
  // This ensures URL navigation (back/forward) works correctly while preserving
  // the user's current tab when they log in/out.
  useEffect(() => {
    if (!SHOW_SAA_MEETINGS_TAB && searchParams.get('tab') === 'meetings') {
      setSearchParams({ tab: 'resources' }, { replace: true });
      return;
    }
    const tabFromUrl = getInitialTab();
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  
  // Handler for tab clicks - updates both state and URL. tab1=steps, tab2=resources, tab3=meetings.
  const handleTabClick = (tab: 'tab1' | 'tab2' | 'tab3') => {
    setActiveTab(tab);
    const tabParam = tab === 'tab1' ? 'steps' : tab === 'tab2' ? 'resources' : 'meetings';
    setSearchParams({ tab: tabParam });
  };
  
  // Step progress state (for Tab 1)
  const [stepProgress, setStepProgress] = useState<Record<number, ProgressItem | null>>({});
  const [isInitializing, setIsInitializing] = useState(true);
  const stepProgressRef = useRef<Record<number, ProgressItem | null>>({});

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: true }
  ];

  // Track page visit
  usePageTracking('SAA Homepage');

  // Load program config on mount; site config also refreshes when opening Meetings tab
  useEffect(() => {
    loadConfig();
  }, []);

  const refreshSiteConfig = useCallback(() => {
    getSiteConfig().then((site) => {
      setMeetingsEnabled(site.meetings_enabled);
      setWorkshopsTabShowFullFinder(!!site.workshops_tab_show_full_finder);
    });
  }, []);

  useEffect(() => {
    refreshSiteConfig();
  }, [refreshSiteConfig]);

  useEffect(() => {
    if (activeTab === 'tab3') {
      refreshSiteConfig();
    }
  }, [activeTab, refreshSiteConfig]);

  // Emergency override: allow a specific logged-in user to see meetings regardless
  // of the site-level meetings_enabled flag (useful for testing on localhost/prod).
  const meetingsGateBypass =
    (user?.email || '').toLowerCase() === 'matt.taylor64@gmail.com';
  const meetingsAllowed = meetingsGateBypass || meetingsEnabled;
  /** Full meeting finder hidden for launch; builder can re-enable via Admin site config while logged in as admin. */
  const showFullMeetingsFinder =
    meetingsAllowed && workshopsTabShowFullFinder && !!user && (user?.isAdmin || meetingsGateBypass);

  // Load step progress when user is available and tab1 is active (reload on navigation back)
  useEffect(() => {
    if (user && activeTab === 'tab1') {
      loadStepProgress();
    }
  }, [user, activeTab, location.pathname, location.search, stepWorkspaceRevision]);

  // Keep ref in sync with state
  useEffect(() => {
    stepProgressRef.current = stepProgress;
  }, [stepProgress]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const programConfig = await AdminService.getProgramConfig('saa');
      setConfig(programConfig);
      clearError();
    } catch (err: any) {
      console.error('Error loading SAA config:', err);
      handleError(err, 'Failed to load page configuration. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadStepProgress = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    try {
      await progressService.initializeProgress();
      await progressService.migrateGettingStartedIfNeeded();
      const progress = await progressService.getAllStepProgress('SAA', '12-steps');
      setStepProgress(progress);
    } catch (err) {
      console.error('Error loading step progress:', err);
    } finally {
      setIsInitializing(false);
    }
  };

  const getStepStatus = (stepNumber: number): 'not-started' | 'in-progress' | 'completed' => {
    if (!user) return 'not-started';
    
    const progress = stepProgress[stepNumber];
    if (!progress) return 'not-started';
    
    if (progress.completed) return 'completed';
    
    if (progress.data && progress.data.actions && Array.isArray(progress.data.actions)) {
      const hasCompletedAction = progress.data.actions.some((action: any) => action.completed);
      if (hasCompletedAction) return 'in-progress';
    }
    
    return 'not-started';
  };

  const handleStepClick = (stepNumber: number) => {
    navigate(`/saa/12-steps/step-${stepNumber}`);
  };

  const handleGettingStartedClick = () => {
    navigate('/saa/12-steps/getting-started');
  };

  const getGettingStartedStatus = (): 'not-started' | 'in-progress' | 'completed' => {
    return getStepStatus(0);
  };

  const renderPanel = (panel: any) => {
    switch (panel.format) {
      case 'list_with_images':
        return (
          <PanelListWithImages
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
            iconUrl={panel.icon_url}
            accentColor={panel.accent_color}
            showItemCount={panel.show_item_count !== false}
          />
        );
      case 'list_links':
        return (
          <PanelListLinks
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
            iconUrl={panel.icon_url}
            accentColor={panel.accent_color}
            showItemCount={panel.show_item_count !== false}
          />
        );
      case 'list_text':
        return (
          <PanelListText
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
            iconUrl={panel.icon_url}
            accentColor={panel.accent_color}
            showItemCount={panel.show_item_count !== false}
          />
        );
      case 'list_videos':
        return (
          <PanelListVideos
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
            iconUrl={panel.icon_url}
            accentColor={panel.accent_color}
            showItemCount={panel.show_item_count !== false}
          />
        );
      default:
        return null;
    }
  };

  // Step data with descriptions
  const steps = [
    {
      number: 1,
      title: 'We admitted we were powerless over our addictive sexual behavior - that our lives had become unmanageable.',
      description: 'The first step involves honest self-assessment and admitting powerlessness over sexual addiction.',
      path: '/saa/12-steps/step-1'
    },
    {
      number: 2,
      title: 'Came to believe that a Power greater than ourselves could restore us to sanity.',
      description: 'Developing faith in a higher power that can help restore mental and emotional health.',
      path: '/saa/12-steps/step-2'
    },
    {
      number: 3,
      title: 'Made a decision to turn our will and our lives over to the care of God as we understood God.',
      description: 'Surrendering control and trusting in a higher power for guidance and strength.',
      path: '/saa/12-steps/step-3'
    },
    {
      number: 4,
      title: 'Made a searching and fearless moral inventory of ourselves.',
      description: 'A thorough self-examination of resentments, fears, and harms done to others.',
      path: '/saa/12-steps/step-4'
    },
    {
      number: 5,
      title: 'Admitted to God, to ourselves, and to another human being the exact nature of our wrongs.',
      description: 'Sharing the inventory with a trusted person and experiencing the relief of honesty.',
      path: '/saa/12-steps/step-5'
    },
    {
      number: 6,
      title: 'Were entirely ready to have God remove all these defects of character.',
      description: 'Becoming willing to let go of character defects and negative patterns.',
      path: '/saa/12-steps/step-6'
    },
    {
      number: 7,
      title: 'Humbly asked God to remove our shortcomings.',
      description: 'Asking for help in removing character defects and developing humility.',
      path: '/saa/12-steps/step-7'
    },
    {
      number: 8,
      title: 'Made a list of all persons we had harmed and became willing to make amends to them all.',
      description: 'Identifying those harmed by our actions and preparing to make amends.',
      path: '/saa/12-steps/step-8'
    },
    {
      number: 9,
      title: 'Made direct amends to such people wherever possible, except when to do so would injure them or others.',
      description: 'Taking action to repair relationships and make amends where appropriate.',
      path: '/saa/12-steps/step-9'
    },
    {
      number: 10,
      title: 'Continued to take personal inventory and when we were wrong promptly admitted it.',
      description: 'Ongoing self-examination and immediate admission of mistakes.',
      path: '/saa/12-steps/step-10'
    },
    {
      number: 11,
      title: 'Sought through prayer and meditation to improve our conscious contact with God as we understood God, praying only for knowledge of God\'s will for us and the power to carry that out.',
      description: 'Developing spiritual practices and seeking guidance through prayer and meditation.',
      path: '/saa/12-steps/step-11'
    },
    {
      number: 12,
      title: 'Having had a spiritual awakening as the result of these steps, we tried to carry this message to sex addicts and to practice these principles in all our affairs.',
      description: 'Sharing the message of recovery and living by spiritual principles in all areas of life.',
      path: '/saa/12-steps/step-12'
    }
  ];

  if (loading) {
    return (
      <div className={styles.saaHomePage}>
        <Header 
          breadcrumbs={breadcrumbs}
          user={user}
          onLogout={onLogout}
          onLogin={onLogin}
        />
        <main className={styles.mainContent}>
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  if (errorMessage && authError) {
    return (
      <div className={styles.saaHomePage}>
        <Header 
          breadcrumbs={breadcrumbs}
          user={user}
          onLogout={onLogout}
          onLogin={onLogin}
        />
        <main className={styles.mainContent}>
          <div className={styles.errorState}>
            <AuthErrorDisplay
              error={errorMessage}
              onLogin={onLogin}
              onRefresh={() => window.location.reload()}
            />
          </div>
        </main>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={styles.saaHomePage}>
        <Header 
          breadcrumbs={breadcrumbs}
          user={user}
          onLogout={onLogout}
          onLogin={onLogin}
        />
        <main className={styles.mainContent}>
          <div className={styles.errorState}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage || 'Failed to load page configuration'}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage || 'Failed to load page configuration'}</p>
                <button onClick={loadConfig} className={styles.retryButton}>
                  Retry
                </button>
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Get logo URL from config, or fallback to default
  const getLogoUrl = (): string => {
    if (config?.logo_url) {
      // Fix old URL format if needed (recoverycommons.com.s3.amazonaws.com -> s3.amazonaws.com/recoverycommons.com)
      let logoUrl = config.logo_url;
      if (logoUrl.includes('recoverycommons.com.s3.amazonaws.com')) {
        logoUrl = logoUrl.replace('https://recoverycommons.com.s3.amazonaws.com/', 'https://s3.amazonaws.com/recoverycommons.com/');
      }
      return logoUrl;
    }
    // Fallback to default (using path-style to avoid SSL issues)
    return 'https://s3.amazonaws.com/recoverycommons.com/programs/saa/icons/from-shame-to-grace.png';
  };

  const saaIconUrl = getLogoUrl();

  return (
    <div className={styles.saaHomePage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      <main className={styles.mainContent}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <div className={styles.titleRow}>
            <h1 className={styles.saaTitle}>{config.title}</h1>
            {saaIconUrl && (
              <img 
                src={saaIconUrl} 
                alt="SAA" 
                className={styles.saaIcon}
                onError={(e) => {
                  // Hide image if it fails to load
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
          </div>
          <div className={styles.aboutContent}>
            {!aboutExpanded ? (
              <>
                <div className={styles.aboutPreview}>
                  <span className={styles.aboutPreviewText}>{config.subtitle}</span>
                  {' '}
                  <button className={styles.readMoreBtn} onClick={() => setAboutExpanded(true)}>
                    read more
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={styles.aboutFull}>
                  <p>{config.subtitle}</p>
                  <p>This page provides everything you need for your SAA recovery journey. Use the tabs below to:</p>
                  <ul className={styles.aboutList}>
                    <li><strong>Videos, Articles, Books, & Quotes</strong> - Explore recovery videos, articles, book recommendations, and quotes to support your journey.</li>
                    <li><strong>Meetings</strong> - Find local and online SAA meetings, learn about workshop opportunities, and connect with others in recovery.</li>
                    <li><strong>Work my steps</strong> - Track your progress through the 12 steps of recovery. Each step includes guided actions, recovery literature, and tools to help you work through the program systematically.</li>
                  </ul>
                  <p>SAA is not allied with any sect, denomination, politics, organization, or institution. We do not wish to engage in any controversy, and we neither endorse nor oppose any causes. Our primary purpose is to stay sexually sober and help other sex addicts achieve sobriety.</p>
                  <p>The only requirement for membership is a desire to stop addictive sexual behavior. There are no dues or fees for SAA membership. We are self-supporting through our own contributions.</p>
                </div>
                <button className={styles.readMoreBtn} onClick={() => setAboutExpanded(false)}>
                  show less
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tabs: order Resources | Meetings | Work my steps. Default tab = Resources. */}
        <div className={styles.tabsContainer}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'tab2' ? styles.activeTab : ''}`}
              onClick={() => handleTabClick('tab2')}
              style={activeTab === 'tab2' && config.tab2?.accent_color ? {
                color: config.tab2.accent_color,
                borderBottomColor: config.tab2.accent_color
              } : {}}
            >
              {config.tab2?.icon_url && (
                <img src={config.tab2.icon_url} alt="" className={styles.tabIcon} onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }} />
              )}
              {config.tab2?.name || 'Resources'}
            </button>
            {SHOW_SAA_MEETINGS_TAB && (
            <button
              className={`${styles.tab} ${activeTab === 'tab3' ? styles.activeTab : ''}`}
              onClick={() => handleTabClick('tab3')}
              style={activeTab === 'tab3' && config.tab3?.accent_color ? {
                color: config.tab3.accent_color,
                borderBottomColor: config.tab3.accent_color
              } : {}}
            >
              {config.tab3?.icon_url && (
                <img src={config.tab3.icon_url} alt="" className={styles.tabIcon} onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }} />
              )}
              {config.tab3?.name || 'Meetings'}
            </button>
            )}
            <button
              className={`${styles.tab} ${activeTab === 'tab1' ? styles.activeTab : ''}`}
              onClick={() => handleTabClick('tab1')}
              style={activeTab === 'tab1' && config.tab1?.accent_color ? {
                color: config.tab1.accent_color,
                borderBottomColor: config.tab1.accent_color
              } : {}}
            >
              {config.tab1?.icon_url && (
                <img src={config.tab1.icon_url} alt="" className={styles.tabIcon} onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }} />
              )}
              Work my steps
            </button>
          </div>

          {/* Tab 1 Content: Work My Steps */}
          {activeTab === 'tab1' && (
            <div className={styles.tabContent}>
              <div className={styles.stepsTabHeader}>
                <div className={styles.stepsTabTitleRow}>
                  <h2 className={styles.stepsTabTitle}>Your SAA 12 Step checklist</h2>
                  <StepHelpButton stepKey="steps-tab" />
                </div>
              </div>

              {isInitializing && user && (
                <div className={styles.loadingMessage}>
                  <div>⏳ Loading your progress...</div>
                  <div className={styles.loadingSubtext}>Please wait while we retrieve your step data</div>
                </div>
              )}

              {!user && (
                <div className={styles.loginPrompt}>
                  <div className={styles.loginPromptContent}>
                    <h2>Sign in to track your 12 step work</h2>
                    <p>
                      Create an account or sign in to track your progress through the 12 steps and receive
                      personalized guidance in your recovery journey.
                    </p>
                    <button onClick={onLogin} className={styles.loginButton}>
                      Sign in / Create account
                    </button>
                  </div>
                </div>
              )}

              {!isInitializing && user && (
                <>
                  <JourneyOverview user={user} stepProgress={stepProgress} />
                  <div className={styles.stepsGrid}>
                    <StepCard
                      label="Getting Started"
                      title="Introduction to SAA — addiction, fellowship, sponsor, and your 3 Circles"
                      description="Readings, video, and personal tools before you begin Step One."
                      status={getGettingStartedStatus()}
                      isLoggedIn={!!user}
                      stepPath="/saa/12-steps/getting-started"
                      onStepClick={handleGettingStartedClick}
                      onLoginClick={onLogin}
                    />
                    {steps.map((step) => (
                      <StepCard
                        key={step.number}
                        stepNumber={step.number}
                        title={step.title}
                        description={step.description}
                        status={getStepStatus(step.number)}
                        isLoggedIn={!!user}
                        stepPath={step.path}
                        onStepClick={() => handleStepClick(step.number)}
                        onLoginClick={onLogin}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tab 2 Content: Resources & Reading Panels */}
          {activeTab === 'tab2' && (
            <div className={styles.tabContent}>
              {config.tab2.header_text && (
                <p className={styles.tab2Header}>{config.tab2.header_text}</p>
              )}
              <div className={styles.panelsContainer}>
                {config.tab2?.panels
                  ?.sort((a: any, b: any) => a.order - b.order)
                  .map((panel: any) => renderPanel(panel))}
              </div>
            </div>
          )}

          {/* Tab 3 Content: Meetings */}
          {activeTab === 'tab3' && (
            <div className={styles.tabContent}>
              {!meetingsAllowed ? (
                <div className={styles.underConstruction}>
                  <h2>Under construction</h2>
                  <p>Meetings and workshops are coming soon. Check back later.</p>
                </div>
              ) : showFullMeetingsFinder ? (
                <MeetingsTabContent
                  user={user}
                  onLogin={onLogin}
                  headerText={config.tab3?.header_text}
                />
              ) : (
                <>
                  {config.tab3?.header_text && (
                    <p className={styles.tab2Header}>{config.tab3.header_text}</p>
                  )}
                  <Workshop2026MeetingsCard />
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SAAHomePage;
